import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, Truck, Clock, Info, ChevronLeft, ChevronRight } from 'lucide-react';
import { WA_NUMBER } from '../data/products';
import { newCombos, NewCombo } from '../data/newCombos';
import { WhatsAppIcon } from './icons/WhatsAppIcon';
import { NewComboModal } from './NewComboModal';
export function NewArrivalBanner() {
  const [detailCombo, setDetailCombo] = useState<NewCombo | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const scrollToIndex = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(index, newCombos.length - 1));
    track.scrollTo({
      left: track.clientWidth * clamped,
      behavior: 'smooth'
    });
  };
  const handleScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const index = Math.round(track.scrollLeft / track.clientWidth);
    if (index !== activeIndex) setActiveIndex(index);
  };

  // Auto-advance every 5s. Pauses while the detail modal is open, while the
  // pointer rests on the carousel, and when the user prefers reduced motion.
  useEffect(() => {
    if (detailCombo || isPaused) return;
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const timer = window.setInterval(() => {
      const track = trackRef.current;
      if (!track) return;
      const next = (activeIndex + 1) % newCombos.length;
      track.scrollTo({
        left: track.clientWidth * next,
        behavior: 'smooth'
      });
    }, 5000);
    return () => window.clearInterval(timer);
  }, [activeIndex, detailCombo, isPaused]);
  return <section className="relative bg-gradient-to-b from-dark-900 via-[#151a20] to-dark-900 py-14 md:py-20 px-5 overflow-hidden" data-id="element-204" data-mp="34pmuz5u0bluu">
      {/* Cool blue-grey wash pulled from the product photo */}
      <div className="absolute inset-0 pointer-events-none" style={{
      backgroundImage: 'radial-gradient(ellipse at 50% 40%, rgba(176,196,222,0.12) 0%, transparent 65%)'
    }} data-id="element-205" data-mp="2zx3g85jl2kog" />

      <div className="max-w-[1100px] mx-auto relative z-10" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} onFocusCapture={() => setIsPaused(true)} onBlurCapture={() => setIsPaused(false)} onTouchStart={() => setIsPaused(true)} data-id="element-206" data-mp="oc4d68ydudcr">
        <div className="flex items-center justify-between gap-4 mb-6 md:mb-8" data-id="element-207" data-mp="kb0xpnk8yjc2">
          <div className="inline-flex items-center gap-2 bg-alphaGold text-dark-900 px-4 py-1.5 rounded-full text-xs md:text-sm font-black uppercase tracking-[2px]" data-id="element-208" data-mp="5d8jcyqfs0zg">
            <Sparkles className="w-4 h-4" data-id="element-209" data-mp="admu4ojf9p16" />
            Nuevo Ingreso
          </div>

          <div className="hidden md:flex items-center gap-2" data-id="element-210" data-mp="1zrj6n2rdun34">
            <button type="button" onClick={() => scrollToIndex(activeIndex - 1)} disabled={activeIndex === 0} aria-label="Combo anterior" className="w-10 h-10 rounded-full border border-white/20 text-[#dbe4ef] flex items-center justify-center transition-colors duration-200 hover:border-alphaGold hover:text-alphaGold disabled:opacity-30 disabled:hover:border-white/20 disabled:hover:text-[#dbe4ef]" data-id="element-211" data-mp="24ort3fjkdda6">
              <ChevronLeft className="w-5 h-5" data-id="element-212" data-mp="bsfpnlxrt34o" />
            </button>
            <button type="button" onClick={() => scrollToIndex(activeIndex + 1)} disabled={activeIndex === newCombos.length - 1} aria-label="Combo siguiente" className="w-10 h-10 rounded-full border border-white/20 text-[#dbe4ef] flex items-center justify-center transition-colors duration-200 hover:border-alphaGold hover:text-alphaGold disabled:opacity-30 disabled:hover:border-white/20 disabled:hover:text-[#dbe4ef]" data-id="element-213" data-mp="butmo0c0gpev">
              <ChevronRight className="w-5 h-5" data-id="element-214" data-mp="2k0m1hzjw5xot" />
            </button>
          </div>
        </div>

        <div ref={trackRef} onScroll={handleScroll} className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth -mx-5 px-5 md:mx-0 md:px-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden" data-id="element-215" data-mp="2ozouumqiefgy">
          {newCombos.map(combo => <article key={combo.id} className="snap-center shrink-0 w-full pr-5 md:pr-0" data-id="element-216" data-mp="21acn7ratyfgr">
              <div className="grid grid-cols-1 md:grid-cols-2 md:grid-rows-[auto_auto] gap-x-8 lg:gap-x-12 gap-y-6 items-center" data-id="element-217" data-mp="26a8qtpfm84vs">
                {/* Heading — sits directly above the photo on mobile */}
                <div className="order-1 md:col-start-2 md:row-start-1 md:self-end text-center md:text-left" data-id="element-218" data-mp="2fzsct3ymkeuv">
                  <h2 className="text-3xl md:text-4xl lg:text-[2.9rem] font-black text-white uppercase leading-[1.05] tracking-tight" data-id="element-219" data-mp="1ij3cxft8jmo4">
                    {combo.headline}{' '}
                    <span className="text-alphaGold" data-id="element-220" data-mp="4mjw7nljwclq">
                      {combo.headlineAccent}
                    </span>
                  </h2>
                </div>

                {/* Product photo */}
                <div className="order-2 md:col-start-1 md:row-start-1 md:row-span-2 md:self-center" data-id="element-221" data-mp="3rqzra1xoqbsy">
                  <div className={combo.imageOnLightCard ? 'rounded-2xl bg-white p-4 shadow-[0_25px_45px_rgba(0,0,0,0.55)]' : ''} data-id="element-222" data-mp="3mseopvuyng1c">
                    <img src={combo.image} alt={combo.imageAlt} className={`w-full h-auto block ${combo.imageOnLightCard ? '' : 'drop-shadow-[0_25px_45px_rgba(0,0,0,0.7)]'}`} data-id="element-223" data-mp="391jnn5qpe38b" />
                  </div>
                </div>

                {/* Details */}
                <div className="order-3 md:col-start-2 md:row-start-2 md:self-start text-center md:text-left" data-id="element-224" data-mp="2u5u2f9tcdu4n">
                  <ul className="space-y-2 mb-6 text-left inline-block md:block" data-id="element-225" data-mp="2kflopvithj86">
                    {combo.includes.map(item => <li key={item} className="flex items-start gap-2.5 text-[#dbe4ef] text-base md:text-lg font-semibold" data-id="element-226" data-mp="2bg8h78e974q3">
                        <span className="text-alphaGold font-black leading-7" data-id="element-227" data-mp="337egjmm5gyeb">
                          ✓
                        </span>
                        <span data-id="element-228" data-mp="385g1kd34z7sf">{item}</span>
                      </li>)}
                  </ul>

                  <div className="mb-6" data-id="element-229" data-mp="2uel0vo2sjwzx">
                    <div className="flex items-end gap-3 justify-center md:justify-start" data-id="element-230" data-mp="2ygocccpgn1l7">
                      <span className="block text-[3rem] md:text-[3.6rem] leading-none font-black text-alphaGold drop-shadow-[0_4px_20px_rgba(212,160,23,0.35)]" data-id="element-231" data-mp="2yebndflabqyn">
                        ${combo.price.toLocaleString('es-UY')}
                      </span>
                      {combo.originalPrice && <span className="text-xl md:text-2xl font-bold text-[#7f8fa3] line-through mb-1" data-id="element-232" data-mp="1aix2hzoeycv6">
                          ${combo.originalPrice.toLocaleString('es-UY')}
                        </span>}
                    </div>
                    <span className="block text-xs md:text-sm uppercase tracking-[3px] text-[#9fb0c4] font-bold mt-2" data-id="element-233" data-mp="1k98iikldgcif">
                      Precio único · Por tiempo limitado
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-7 justify-center md:justify-start" data-id="element-234" data-mp="rm5jlrcs9zrn">
                    {combo.freeShipping && <div className="inline-flex items-center gap-2 bg-alphaGold/10 border border-alphaGold/40 text-alphaGold px-3 py-1.5 rounded-full text-xs md:text-sm font-bold" data-id="element-235" data-mp="11hfpwra7stb8">
                        <Truck className="w-4 h-4" data-id="element-236" data-mp="2cwcfc2ab9kj4" />
                        Envío gratis en Montevideo
                      </div>}
                    <div className="inline-flex items-center gap-2 bg-white/5 border border-white/15 text-[#c3d0e0] px-3 py-1.5 rounded-full text-xs md:text-sm font-bold" data-id="element-237" data-mp="a8vyhdehpxkw">
                      <Clock className="w-4 h-4" data-id="element-238" data-mp="k7bunusdjx19" />
                      Stock limitado
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center gap-3 justify-center md:justify-start" data-id="element-239" data-mp="typwwabpnay3">
                    <a href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(combo.waMessage)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-alphaGold hover:bg-[#e6b835] text-dark-900 font-black uppercase tracking-wider text-sm md:text-base py-4 px-7 rounded-lg shadow-[0_8px_25px_rgba(212,160,23,0.35)] transition-colors duration-200" data-id="element-240" data-mp="xzrzn14u654t">
                      <WhatsAppIcon className="w-5 h-5" data-id="element-241" data-mp="326kzso16o2a4" />
                      Quiero este combo
                    </a>

                    <button type="button" onClick={() => setDetailCombo(combo)} className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-alphaGold text-[#dbe4ef] hover:text-alphaGold font-bold uppercase tracking-wider text-sm py-4 px-6 rounded-lg transition-colors duration-200" data-id="element-242" data-mp="2xeumqaav30f0">
                      <Info className="w-4 h-4" data-id="element-243" data-mp="3g6ney2i3whlo" />
                      Ver más información
                    </button>
                  </div>
                </div>
              </div>
            </article>)}
        </div>

        {/* Slide indicators */}
        <div className="flex items-center justify-center gap-2.5 mt-8" data-id="element-244" data-mp="1zsdrrbploywf">
          {newCombos.map((combo, index) => <button key={combo.id} type="button" onClick={() => scrollToIndex(index)} aria-label={`Ver ${combo.name}`} aria-current={index === activeIndex} className={`h-2 rounded-full transition-all duration-200 ${index === activeIndex ? 'w-8 bg-alphaGold' : 'w-2 bg-white/25 hover:bg-white/50'}`} data-id="element-245" data-mp="1urpzyntjse15" data-mpi={index} />)}
        </div>

        <p className="md:hidden text-center text-[#7f8fa3] text-xs uppercase tracking-[2px] font-bold mt-3" data-id="element-246" data-mp="2nzr3oteg1uip">
          Deslizá para ver más combos
        </p>
      </div>

      <NewComboModal combo={detailCombo} onClose={() => setDetailCombo(null)} data-id="element-247" data-mp="2xyortv7yjm16" />
    </section>;
}